import { useDroppable } from '@dnd-kit/core';
import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import type { ColunaSpec } from './statusMap';

interface Props extends ColunaSpec {
  count: number;
  children: ReactNode;
  empty: string;
}

const acentoVar: Record<ColunaSpec['acento'], string> = {
  amber: 'var(--col-pagamento)',
  moss: 'var(--col-pago)',
  sage: 'var(--col-separacao)',
};
const dotClass: Record<ColunaSpec['acento'], string> = {
  amber: 'bg-amber-500',
  moss: 'bg-moss-300',
  sage: 'bg-sage-500',
};

export function KanbanColumn({ id, titulo, acento, count, children, empty }: Props) {
  const { setNodeRef, isOver } = useDroppable({ id: `col-${id}`, data: { coluna: id } });

  return (
    <section
      ref={setNodeRef}
      className={cn(
        'relative flex flex-col bg-panel border border-[var(--border)] rounded-lg p-s5 min-h-[420px] h-full',
        'transition-[background,border-color] duration-base ease-soft',
        isOver && 'border-[var(--border-strong)] bg-[rgba(255,255,255,0.012)]',
      )}
    >
      <header className="flex items-center justify-between pb-s4 mb-s4 border-b border-[var(--border-subtle)]">
        <div className="flex items-center gap-s3">
          <span
            className={cn('w-[8px] h-[8px] rounded-full', dotClass[acento])}
            style={{ boxShadow: '0 0 0 3px rgba(255,255,255,0.04)' }}
          />
          <h2 className="font-body font-semibold text-[12px] uppercase tracking-[0.1em] text-fg-2">
            {titulo}
          </h2>
        </div>
        <span className="font-mono text-[12px] text-fg-3 tnum">{count}</span>
      </header>

      <div className="flex flex-col gap-s3 flex-1 overflow-y-auto pr-s2 -mr-s2 [&>*]:animate-cardEnter">
        {count === 0 ? (
          <p className="font-display italic text-[16px] text-fg-3 mt-s5 text-center">
            {empty}
          </p>
        ) : (
          children
        )}
      </div>

      {/* indicação visual sutil de drop ativo */}
      {isOver && (
        <div
          className="pointer-events-none absolute inset-0 rounded-lg"
          style={{
            boxShadow: `inset 0 0 0 1px ${acentoVar[acento]}`,
            opacity: 0.4,
          }}
          aria-hidden
        />
      )}
    </section>
  );
}

export default KanbanColumn;
